import {
  bondRatePercent,
  type BondPremiumBasis,
  type BondSubmission,
} from "@/lib/brokerBonds";
import { formatBondCurrency } from "@/lib/bondValueDisplay";
import { estimateFromOrder } from "@/lib/wonBid";

// BRK-S328 — the estimated Performance bond premium (Sprint 10 rules 3 and
// 6). There is no quote: the premium is the account's active program rate ×
// the contract price, and the broker sees it twice — on the indication, at the
// ESTIMATED price, and on the won-bid screen, recomputed live from the FINAL
// price being confirmed. Both are DISPLAY ONLY; nothing here reaches the wire.
//
// ⚠️ THE RATE IS PER $1,000, not a percent: `amount = price × rate / 1000`,
// and the rate is shown through `bondRatePercent` (÷10), the one conversion.
//
// ⚠️ ABSENT, NEVER $0. A missing rate or price means no figure at all: a $0
// premium is a claim (a free bond), and only bid bonds are free (rule 1).

/** A premium the portal can show, with the inputs its formula line names. */
export interface PremiumFigure {
  amount: number;
  contractPrice: number;
  /** Per $1,000; null when PMS published an amount without its rate. */
  ratePer1000: number | null;
  basis: BondPremiumBasis;
}

const positive = (v: unknown): v is number =>
  typeof v === "number" && Number.isFinite(v) && v > 0;

/**
 * `price × rate / 1000`, rounded to the cent — or null when either input is
 * missing, zero or not a number. A formatted string is refused rather than
 * parsed (D-10, both directions).
 */
export function performancePremium(
  ratePer1000: unknown,
  contractPrice: unknown,
): number | null {
  if (!positive(ratePer1000) || !positive(contractPrice)) return null;
  // × 100 / 1000 = / 10: one division, so the cents round on the figure a
  // person would compute rather than on an accumulated double.
  return Math.round((contractPrice * ratePer1000) / 10) / 100;
}

/**
 * The order's estimated premium for the indication: PMS's own
 * `estimatedPremium` when it publishes a usable one (PMS-S720), else computed
 * from the published rate — the order's `program.rate`, falling back to the
 * caller's (the decision's `terms.ratePer1000`) — and the order's estimated
 * contract price. Null when an input is missing.
 */
export function orderPremium(
  order: BondSubmission,
  fallbackRatePer1000: number | null | undefined,
): PremiumFigure | null {
  const programRate = order.program?.rate;
  const rate = positive(programRate)
    ? programRate
    : positive(fallbackRatePer1000)
      ? fallbackRatePer1000
      : null;
  const published = order.estimatedPremium;
  if (
    published &&
    positive(published.amount) &&
    positive(published.contractPrice)
  ) {
    return {
      amount: published.amount,
      contractPrice: published.contractPrice,
      ratePer1000: rate,
      basis:
        published.basis === "final_contract_price"
          ? "final_contract_price"
          : "estimated_contract_price",
    };
  }
  const price = estimateFromOrder(order);
  const amount = performancePremium(rate, price);
  if (amount === null || price === null || rate === null) return null;
  return {
    amount,
    contractPrice: price,
    ratePer1000: rate,
    basis: "estimated_contract_price",
  };
}

/** Currency for display: thousands separators, and cents only when the
 *  figure has any — "$11,250", "$2,612.50". */
export function formatPremium(amount: number): string {
  if (Number.isInteger(amount)) return formatBondCurrency(amount);
  return amount.toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

const BASIS_WORDS: Record<BondPremiumBasis, string> = {
  estimated_contract_price: "estimated contract price",
  final_contract_price: "final contract price",
};

/** The grey line under the figure: "$450,000 estimated contract price ×
 *  2.50% rate". Without a rate it says what the figure rests on instead. */
export function premiumFormula(premium: PremiumFigure): string {
  const price = formatBondCurrency(premium.contractPrice);
  const basis = BASIS_WORDS[premium.basis];
  if (premium.ratePer1000 === null) return `At your ${basis} of ${price}`;
  return `${price} ${basis} × ${bondRatePercent(premium.ratePer1000)}% rate`;
}

/** The won-bid screen's label and terms line (rule 5: one invoice per order,
 *  45-day terms, billed direct to the contractor). */
export const PREMIUM_TO_INVOICE = "Premium to be invoiced";
export const PREMIUM_TERMS = "Billed to the contractor on 45-day terms.";
export const ESTIMATED_PREMIUM = "Estimated Performance bond premium";
